import React, { useState } from 'react'
import {
  Box, MenuItem, Paper, Select, TextField, Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useListPeopleQuery } from '../../redux/growthosBrandsApiSlice'
import { useGetSmeTargetsQuery, usePatchPersonMutation, useSetSmeTargetMutation } from '../../redux/growthosPocsApiSlice'
import { GX_BORDER, GX_INK_500, GX_INK_900, GX_CARD_SX, GX_PAGE_SX } from '../growthos-shared/GrowthOsUi'
import { GxPagedTable } from '../growthos-shared/GxPagedTable'

const TEAMS = ['SME', 'ENTERPRISE', 'INSIDE_SALES', 'KAM', 'MARKETING', 'MANAGEMENT', 'OTHER']
const thisMonth = () => new Date().toISOString().slice(0, 7)

const CARD_SX = { ...GX_CARD_SX, overflow: 'hidden' }

// Inactive people are dimmed exactly as the real app's opacity-50 treatment.
const dim = (active, node) => (active ? node : <Box sx={{ opacity: 0.5 }}>{node}</Box>)

/**
 * GrowthOS — Sales POCs. Names come from HubSpot owners (never typed by hand) — classify each
 * person's team here; SME/Enterprise scoping across dashboards (not built in this migration
 * yet) reads this column. No external dependency here — this is pure ProShip-internal
 * classification of already-synced people, so nothing needed deferring.
 */
export default function PocsPage() {
  const { enqueueSnackbar } = useSnackbar()
  // isLoading (first load only), not isFetching — a refetch after each inline edit must not
  // blank the table out to "Loading…".
  const { data, isLoading, error, refetch } = useListPeopleQuery()
  const [patchPerson] = usePatchPersonMutation()

  const [month, setMonth] = useState(thisMonth())
  const { data: targetData, isLoading: targetsLoading } = useGetSmeTargetsQuery(month)
  const [setSmeTarget] = useSetSmeTargetMutation()

  const people = data?.data || []
  const targets = targetData?.targets || []

  const managerOptions = people
    .filter((p) => p.active !== false)
    .map((p) => ({ id: p.hubspotOwnerId, fullName: p.fullName }))
    .sort((a, b) => a.fullName.localeCompare(b.fullName))

  const patch = async (id, body, successMsg) => {
    try {
      await patchPerson({ id, body }).unwrap()
      refetch()
      if (successMsg) enqueueSnackbar(successMsg, { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Update failed.', { variant: 'error' })
    }
  }

  const selectSx = { fontSize: 12, minWidth: 140, '& .MuiSelect-select': { py: 0.75 } }
  const peopleColumns = [
    { key: 'name', header: 'Name', cell: (p) => dim(p.active !== false, <Typography component="span" sx={{ fontWeight: 600, fontSize: 12.5 }}>{p.fullName}</Typography>) },
    { key: 'email', header: 'Email', cell: (p) => dim(p.active !== false, <Typography component="span" sx={{ fontSize: 12 }}>{p.email || '—'}</Typography>) },
    {
      key: 'team', header: 'Team',
      cell: (p) => dim(p.active !== false, (
        <Select size="small" value={p.team || ''} displayEmpty sx={selectSx} onChange={(e) => patch(p.id, { team: e.target.value || null })}>
          <MenuItem value=""><em>Unclassified</em></MenuItem>
          {TEAMS.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 12 }}>{t.replace('_', ' ')}</MenuItem>)}
        </Select>
      )),
    },
    {
      key: 'joining', header: 'Joining date',
      cell: (p) => dim(p.active !== false, (
        <TextField
          size="small" type="date" value={p.joiningDate || ''} InputLabelProps={{ shrink: true }}
          onChange={(e) => patch(p.id, { joiningDate: e.target.value || null })}
          inputProps={{ 'aria-label': `Joining date for ${p.fullName}` }}
          sx={{ '& input': { fontSize: 12, py: 0.75 } }}
        />
      )),
    },
    {
      key: 'manager', header: 'Manager',
      title: "Reports-to. A manager transitively sees their whole team's data (recursive). Tag several POCs to one manager to build the team.",
      cell: (p) => dim(p.active !== false, (
        <Select size="small" value={p.managerPersonId || ''} displayEmpty sx={{ ...selectSx, minWidth: 150, maxWidth: 200 }} onChange={(e) => patch(p.id, { managerPersonId: e.target.value || null })}>
          <MenuItem value=""><em>— none —</em></MenuItem>
          {managerOptions.filter((m) => m.id !== p.hubspotOwnerId).map((m) => (
            <MenuItem key={m.id} value={m.id} sx={{ fontSize: 12 }}>{m.fullName}</MenuItem>
          ))}
        </Select>
      )),
    },
    { key: 'reports', header: 'Reports', align: 'right', title: 'Direct reports — people tagged with this person as their manager.', cell: (p) => dim(p.active !== false, p.directReports > 0 ? p.directReports : '—') },
    { key: 'deals', header: 'Deals owned', align: 'right', title: 'Every HubSpot deal owned by this person (all sources).', cell: (p) => dim(p.active !== false, p.dealsOwned) },
    { key: 'brands', header: 'Brands served', align: 'right', title: 'Brands assigned to this person that have shipped in ProShip.', cell: (p) => dim(p.active !== false, p.brandsServed) },
    { key: 'active', header: 'Active', cell: (p) => dim(p.active !== false, p.active !== false ? 'Yes' : 'No') },
  ]

  return (
    <Box sx={GX_PAGE_SX}>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>Sales POCs</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, mb: 3, maxWidth: 800 }}>
        Pulled directly from HubSpot owners on every sync. Set each person&apos;s team here —
        SME vs Enterprise scoping across dashboards reads this column. Set a{' '}
        <strong>joining date</strong> to enable tenure-aware individual performance later.
        Changes save immediately.
      </Typography>

      <Paper variant="outlined" sx={{ ...CARD_SX, mb: 3 }}>
        {error ? (
          <Typography sx={{ fontSize: 13, color: 'error.main', p: 2.5 }}>
            Failed to load people — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
          </Typography>
        ) : isLoading ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', p: 2.5 }}>Loading…</Typography>
        ) : !people.length ? (
          <Typography sx={{ fontSize: 13, color: GX_INK_500, p: 3, textAlign: 'center' }}>
            No people yet — run a HubSpot sync in Data → Connectors to pull owners.
          </Typography>
        ) : (
          // Scrolls inside the card (sticky header, max-height) and paginates, like the reference.
          <Box sx={{ p: 2 }}>
            <GxPagedTable variant="poc" rows={people} rowKey={(p) => p.id} columns={peopleColumns} />
          </Box>
        )}
      </Paper>

      <SmeTargetsCard month={month} setMonth={setMonth} targets={targets} isLoading={targetsLoading} setSmeTarget={setSmeTarget} />
    </Box>
  )
}

function SmeTargetsCard({ month, setMonth, targets, isLoading, setSmeTarget }) {
  const { enqueueSnackbar } = useSnackbar()
  const [edits, setEdits] = useState({})

  const val = (t, field) => {
    const e = edits[t.personId]
    if (e && e[field] !== undefined) return e[field]
    return field === 'rev' ? (t.revenueTarget ?? '') : (t.shipmentsTarget ?? '')
  }
  const setVal = (t, field, v) =>
    setEdits((prev) => ({ ...prev, [t.personId]: { rev: field === 'rev' ? v : val(t, 'rev'), ship: field === 'ship' ? v : val(t, 'ship') } }))

  const save = async (t) => {
    const rev = val(t, 'rev')
    const ship = val(t, 'ship')
    try {
      await setSmeTarget({
        personId: t.personId, month,
        revenueTarget: rev === '' ? null : Number(rev),
        shipmentsTarget: ship === '' ? null : Number(ship),
      }).unwrap()
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Failed to save target.', { variant: 'error' })
    }
  }

  return (
    <Paper variant="outlined" sx={CARD_SX}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: GX_INK_500 }}>
            SME Monthly Targets
          </Typography>
          <Typography sx={{ fontSize: 12, color: GX_INK_500, mt: 0.5, maxWidth: 700 }}>
            Per-POC monthly revenue &amp; shipment goals for the SME dashboard&apos;s Target vs
            Actual. Only SME-tagged POCs appear. Changes save on blur.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: GX_INK_500 }}>Month</Typography>
          <TextField
            size="small" type="month" value={month}
            onChange={(e) => { setMonth(e.target.value || thisMonth()); setEdits({}) }}
            sx={{ '& input': { fontSize: 12 } }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', px: 2.5, pb: 2.5 }}>Loading…</Typography>
      ) : !targets.length ? (
        <Typography sx={{ fontSize: 13, color: GX_INK_500, borderTop: `1px solid ${GX_BORDER}`, px: 2.5, py: 3.5 }}>
          No SME-tagged Sales POCs yet — set a person&apos;s team to <strong>SME</strong> above and they&apos;ll appear here.
        </Typography>
      ) : (
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <GxPagedTable variant="poc"
            rows={targets}
            rowKey={(t) => t.personId}
            columns={[
              { key: 'poc', header: 'Sales POC', cell: (t) => <Typography component="span" sx={{ fontWeight: 600, fontSize: 12.5 }}>{t.fullName}</Typography> },
              {
                key: 'rev', header: 'Revenue target (₹ / month)',
                cell: (t) => (
                  <TextField
                    size="small" type="number" placeholder="—" value={val(t, 'rev')}
                    onChange={(e) => setVal(t, 'rev', e.target.value)} onBlur={() => save(t)}
                    inputProps={{ min: 0, 'aria-label': `Monthly revenue target for ${t.fullName}` }}
                    sx={{ width: 170, '& input': { fontSize: 12, py: 0.75 } }}
                  />
                ),
              },
              {
                key: 'ship', header: 'Shipments target (/ month)',
                cell: (t) => (
                  <TextField
                    size="small" type="number" placeholder="—" value={val(t, 'ship')}
                    onChange={(e) => setVal(t, 'ship', e.target.value)} onBlur={() => save(t)}
                    inputProps={{ min: 0, 'aria-label': `Monthly shipments target for ${t.fullName}` }}
                    sx={{ width: 160, '& input': { fontSize: 12, py: 0.75 } }}
                  />
                ),
              },
            ]}
          />
        </Box>
      )}
    </Paper>
  )
}
